// AHA-47691 convert hand-off: an embedded app asks the HOST to have the master agent
// (the docked assistant panel) do a job. The embed names an INTENT and ids only; it
// never supplies the message, so no free text crosses either window boundary.
//
//   embed  -> host   zoid prop  askMasterAgent({ intent, presentationId, courseId, courseTitle? })
//   host   -> panel  bridge     { type: 'host-request', id, request: { intent, presentationId, item } }
//
// Everything here is pure so both halves (EmbedAppContainer, GlobalChatbotContainer)
// and the store apply the same rules.

export const CONVERT_PRESENTATION_INTENT = 'convert_presentation'
export const SEED_PROMPT_INTENT = 'seed_prompt'
export const IMPORT_FILE_INTENT = 'import_file'
export const MAX_SEED_PROMPT_LENGTH = 4000
// `generate` (AHA-49124): build interactive slides from the file's content, without inserting its pages.
export const IMPORT_FILE_MODES = Object.freeze(['import', 'import_and_generate', 'generate'])

// A queued request that was not acked within this window is dropped, never sent later,
// and the embed is told `false` so it can fall back to its own agent.
export const HOST_REQUEST_TTL_MS = 12000
// The host stops sending (first send, retries, the send after an `iframe-ready`) once
// the request is older than this, although it stays pending until HOST_REQUEST_TTL_MS.
// The gap is the margin for the ack to come back: the panel starts converting the
// moment it posts its ack, so an ack to a send made right before the expiry could land
// after it, the embed would be told `false`, and both assistants would convert.
export const HOST_REQUEST_SEND_WINDOW_MS = 9000
// While a request is pending and the panel has completed the handshake, the SAME id is
// re-sent this often: the panel deliberately does not ack while it cannot take the
// request yet (its flags or its course tools are not ready).
export const HOST_REQUEST_RETRY_MS = 1500
// After a request settled (acked or dropped) the embed is refused for this long, so a
// looping embed cannot keep opening the panel or stacking conversions.
export const HOST_REQUEST_COOLDOWN_MS = 10000

const PRESENTATION_ID_PATTERN = /^\d{1,32}$/
const COURSE_ID_PATTERN = /^[0-9a-zA-Z-]{1,64}$/
const COURSE_TITLE_MAX_LENGTH = 200
// The aha-elearning course EDITOR as the host routes it (see courseEditorPath in
// constant/convertToLesson.constant.js). Exact: `/course/<id>/preview` and
// `/course/<id>/report` are other pages of the same app, not the editor.
const COURSE_EDITOR_ROUTE_PATTERN = /^\/apps\/addons\/aha-elearning\/course\/([0-9a-zA-Z-]{1,64})\/?$/

// The id aha-elearning registers itself under in its embed-app manifest.
export const ELEARNING_APP_ID = 'aha-elearning'

// Display text only (the progress card's heading), never sent on to the assistant.
const parseCourseTitle = (courseTitle) => {
  if (typeof courseTitle !== 'string') return ''
  const trimmed = courseTitle.trim()
  return trimmed.length <= COURSE_TITLE_MAX_LENGTH ? trimmed : `${trimmed.slice(0, COURSE_TITLE_MAX_LENGTH - 1)}…`
}

/**
 * Shape-check what the embed sent. It arrives over postMessage from another origin, so
 * it is untrusted: only the named fields are read, and only as validated strings.
 * `courseTitle` is optional (older embeds omit it): a missing or non-string one is ''.
 *
 * @returns {{ presentationId: string, courseId: string, courseTitle: string } | null} null = refuse.
 */
export function parseAskMasterAgentRequest(request) {
  if (!request || typeof request !== 'object') return null
  const { intent, presentationId, courseId } = request
  if (intent !== CONVERT_PRESENTATION_INTENT) return null
  if (typeof presentationId !== 'string' || !PRESENTATION_ID_PATTERN.test(presentationId)) return null
  if (typeof courseId !== 'string' || !COURSE_ID_PATTERN.test(courseId)) return null
  return { presentationId, courseId, courseTitle: parseCourseTitle(request.courseTitle) }
}

/** The course id when `route` is the course editor, else null. */
export function courseIdFromEditorRoute(route) {
  const path = route && typeof route.path === 'string' ? route.path : ''
  const match = COURSE_EDITOR_ROUTE_PATTERN.exec(path)
  return match ? match[1] : null
}

/** Whether `route` is the editor of exactly this course (ids compare case-insensitively). */
export function isCourseEditorRoute(route, courseId) {
  const routeCourseId = courseIdFromEditorRoute(route)
  return Boolean(routeCourseId)
    && typeof courseId === 'string'
    && routeCourseId.toLowerCase() === courseId.toLowerCase()
}

/** The `request` member of the `host-request` bridge message. */
export function buildHostRequest({ presentationId, courseId }) {
  return {
    intent: CONVERT_PRESENTATION_INTENT,
    presentationId,
    item: { kind: 'course', id: courseId },
  }
}

/** The `request` of a `seed_prompt` host request: the user's own typed prompt, optionally sent at once. */
export function buildSeedPromptRequest({ presentationId, text, send = false }) {
  const request = {
    intent: SEED_PROMPT_INTENT,
    text: String(text).trim().substring(0, MAX_SEED_PROMPT_LENGTH),
    send: send === true,
  }
  if (presentationId) request.presentationId = String(presentationId)
  return request
}

/** The `request` of an `import_file` host request; `file` is the shaped entry AI_IMPORT_REQUEST carries in `files[0]`. */
export function buildImportFileRequest({
  presentationId, file, prompt, mode,
}) {
  const {
    hash_id: hashId, fileName, fileSize, contentType,
  } = file || {}
  const request = {
    intent: IMPORT_FILE_INTENT,
    file: {
      hash_id: hashId, fileName, fileSize, contentType,
    },
  }
  if (IMPORT_FILE_MODES.includes(mode)) request.mode = mode
  const trimmedPrompt = typeof prompt === 'string' ? prompt.trim().substring(0, MAX_SEED_PROMPT_LENGTH) : ''
  if (trimmedPrompt) request.prompt = trimmedPrompt
  if (presentationId) request.presentationId = String(presentationId)
  return request
}

/** Whether `request` is one of the intents that is not the course convert hand-off. */
export function isMasterAgentOnlyRequest(request) {
  return Boolean(request) && (request.intent === SEED_PROMPT_INTENT || request.intent === IMPORT_FILE_INTENT)
}

// Unique within this page load even when two are made in the same millisecond.
let hostRequestCounter = 0
/** A fresh `id` for a host request. */
export function createHostRequestId() {
  hostRequestCounter += 1
  return `hr-${Date.now().toString(36)}-${hostRequestCounter}-${Math.random().toString(36).slice(2, 10)}`
}

/** Whether the last request settled less than HOST_REQUEST_COOLDOWN_MS ago. */
export function isHostRequestCoolingDown(settledAt, now = Date.now()) {
  return typeof settledAt === 'number' && now - settledAt < HOST_REQUEST_COOLDOWN_MS
}

/** Whether a pending request ({ queuedAt }) may still be SENT (see HOST_REQUEST_SEND_WINDOW_MS). */
export function isHostRequestSendable(pending, now = Date.now()) {
  return Boolean(pending)
    && typeof pending.queuedAt === 'number'
    && now - pending.queuedAt <= HOST_REQUEST_SEND_WINDOW_MS
}

/** Whether a pending request ({ queuedAt }) has outlived HOST_REQUEST_TTL_MS. */
export function isHostRequestExpired(pending, now = Date.now()) {
  return !pending || typeof pending.queuedAt !== 'number' || now - pending.queuedAt >= HOST_REQUEST_TTL_MS
}
